import React from "react";
import { useChat } from "../hooks/useChat";
import { ChatContainer } from "../styles";
import ConversationList from "./ConversationList";
import MessageArea from "./MessageArea";
import NewConversationModal from "./NewConversationModal";
import {
  StyledInput,
  InputContainer,
  AttachmentPreview,
  AttachmentItem,
  RemoveButton,
} from "../styles";
import { Send, Image as ImageIcon, Paperclip, X } from "lucide-react";

const ChatInterface: React.FC = () => {
  const {
    state,
    currentUser,
    selectConversation,
    sendMessage,
    setTyping,
    startSupportChat,
    startDirectChat,
    loadMoreMessages,
    fetchDirectory,
  } = useChat();
  const [inputText, setInputText] = React.useState("");
  const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
  const [filter, setFilter] = React.useState("all");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [isNewChatModalOpen, setIsNewChatModalOpen] = React.useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const typingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (currentUser?.role === "admin") {
      fetchDirectory(filter);
    }
  }, [filter, fetchDirectory, currentUser?.role]);

  const hasChatPermission =
    currentUser?.role !== "admin" ||
    !currentUser?.roleId ||
    (Array.isArray(currentUser?.permissions) &&
      currentUser.permissions.some((p: string) =>
        p.toLowerCase().startsWith("chat."),
      ));

  if (currentUser && !hasChatPermission) {
    return (
      <div
        style={{
          padding: "40px",
          textAlign: "center",
          width: "100%",
          background: "white",
          borderRadius: "12px",
          border: "1px solid #eee",
          margin: "24px",
        }}
      >
        <h2 style={{ color: "#ff3135", marginBottom: "8px" }}>
          Unauthorized Access
        </h2>
        <p style={{ color: "#666" }}>
          You do not have permission to access the Chat module.
        </p>
      </div>
    );
  }

  const filteredConversations = React.useMemo(() => {
    let existingConvs = state.conversations;

    if (currentUser?.role === "store") {
      existingConvs = existingConvs.filter((c) => c.type === "support");
    } else if (filter !== "all") {
      existingConvs = state.conversations.filter((c) => {
        if (filter === "order") return c.type === "order";
        const other =
          c.participants.find(
            (p) =>
              p.participantId !== currentUser?.id ||
              p.role !== currentUser?.role,
          ) || c.participants[0];
        return other?.role === filter;
      });
    }

    if (searchTerm) {
      existingConvs = existingConvs.filter((c) => {
        if (c.type === "order")
          return `order #${c.orderId}`.includes(searchTerm.toLowerCase());
        const other =
          c.participants.find(
            (p) =>
              p.participantId !== currentUser?.id ||
              p.role !== currentUser?.role,
          ) || c.participants[0];
        return other?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    if (
      currentUser?.role !== "admin" ||
      filter === "order" ||
      state.directory.length === 0
    ) {
      return existingConvs;
    }

    // Get IDs of users we already have open conversations with
    const existingUserIds = new Set(
      state.conversations // Use ALL conversations, not just filtered ones, to avoid duplicate creation
        .map(
          (c) =>
            c.participants.find(
              (p) =>
                p.participantId !== currentUser?.id ||
                p.role !== currentUser?.role,
            )?.participantId,
        )
        .filter(Boolean),
    );

    // Filter directory only to items matching the current tab
    let directoryItems = state.directory.filter((item) => {
      // item.id = users.id (already the user ID for stores too, since backend selectStoreQuery returns id: users.id)
      const targetId = item.id;
      const roleMatches = filter === "all" || item._dirRole === filter;
      return roleMatches && !existingUserIds.has(targetId);
    });
    if (searchTerm) {
      directoryItems = directoryItems.filter((item) => {
        const name = item.storeName || item.fullName || item.name || "";
        return name.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    const mappedDirItems = directoryItems.map((item) => {
      // For ALL roles, `item.id` is the users.id — the backend always selects `id: users.id`
      const targetParticipantId = item.id;

      return {
        id: `dir-${targetParticipantId}`,
        _dirRole: item._dirRole, // keep dirRole on the mapped item so handleSelectConv can use it
        type: "general" as const,
        orderId: null,
        subject: null,
        status: "active" as const,
        metadata: null,
        createdAt: "1970-01-01T00:00:00Z",
        updatedAt: "1970-01-01T00:00:00Z",
        unreadCount: 0,
        participants: [
          {
            id: -1,
            participantType: "user" as const,
            participantId: targetParticipantId,
            role: item._dirRole,
            name:
              item.storeName ||
              item.fullName ||
              item.name ||
              item.username ||
              "User",
            avatar: item.logo || item.image || item.avatar || null,
          },
        ],
      };
    });

    // Final dedup by ID as a safety net (e.g. stale directory items overlapping with active convs)
    const seenIds = new Set<string | number>();
    const merged = [...existingConvs, ...mappedDirItems].filter((c: any) => {
      if (seenIds.has(c.id)) return false;
      seenIds.add(c.id);
      return true;
    });

    // Explicitly sort:
    // 1. Conversations with latest messages first (sorted by date desc)
    // 2. New conversations without messages
    // 3. Directory items at the very bottom
    merged.sort((a: any, b: any) => {
      const isADir = typeof a.id === "string" && a.id.startsWith("dir-");
      const isBDir = typeof b.id === "string" && b.id.startsWith("dir-");

      // Active conversations with any message come before directory items
      // Check for .id specifically because backend may return an object with null fields
      const hasMsgA = !!a.latestMessage?.id;
      const hasMsgB = !!b.latestMessage?.id;

      if (hasMsgA && !hasMsgB) return -1;
      if (!hasMsgA && hasMsgB) return 1;

      // Handle cases where both have or both lack latestMessage
      const timeA = new Date(
        (hasMsgA ? a.latestMessage?.createdAt : null) || a.createdAt || 0,
      ).getTime();
      const timeB = new Date(
        (hasMsgB ? b.latestMessage?.createdAt : null) || b.createdAt || 0,
      ).getTime();

      if (timeA !== timeB) return timeB - timeA;

      // If timestamps are identical, dir items go to bottom
      if (isADir && !isBDir) return 1;
      if (!isADir && isBDir) return -1;

      return 0;
    });

    return merged as any[];
  }, [state.conversations, filter, currentUser, state.directory, searchTerm]);

  const activeConv =
    state.conversations.find((c) => c.id === state.activeConversationId) ||
    null;
  const activeMessages = state.activeConversationId
    ? state.messages[state.activeConversationId] || []
    : [];
  const pagination = state.activeConversationId
    ? state.pagination[state.activeConversationId]
    : null;

  const handleCreateChat = async (participantId: number, role: string) => {
    await startDirectChat(participantId, role);
  };

  const handleSelectConv = async (convId: string | number) => {
    if (typeof convId === "string" && convId.startsWith("dir-")) {
      const realId = parseInt(convId.replace("dir-", ""), 10);
      // Find the item in filteredConversations to get its actual role, not the filter value
      const dirItem = filteredConversations.find((c: any) => c.id === convId);
      const itemRole = dirItem?._dirRole || filter;
      await startDirectChat(realId, itemRole);
    } else {
      selectConversation(convId as number);
    }
  };

  const handleSend = () => {
    if (!inputText.trim() && selectedFiles.length === 0) return;
    sendMessage(inputText, selectedFiles);
    setInputText("");
    setSelectedFiles([]);
    setTyping(false);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
  };

  const handleTyping = (text: string) => {
    setInputText(text);
    if (text.trim().length > 0) {
      setTyping(true);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        setTyping(false);
      }, 2000);
    } else {
      setTyping(false);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles((prev) => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <ChatContainer>
      <ConversationList
        conversations={filteredConversations}
        activeId={state.activeConversationId as number}
        onSelect={handleSelectConv}
        currentUser={currentUser}
        onlineUsers={state.onlineUsers}
        currentFilter={filter}
        onFilterChange={setFilter}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onNewChat={() => setIsNewChatModalOpen(true)}
      />

      {isNewChatModalOpen && (
        <NewConversationModal
          onClose={() => setIsNewChatModalOpen(false)}
          onSelect={handleCreateChat}
          directory={state.directory}
          fetchDirectory={fetchDirectory}
        />
      )}

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <MessageArea
          messages={activeMessages}
          activeConversation={activeConv}
          currentUserId={currentUser?.id || 0}
          currentUserRole={currentUser?.role || ""}
          isTyping={
            state.activeConversationId
              ? state.isTyping[state.activeConversationId]
              : false
          }
          onStartSupport={startSupportChat}
          onLoadMore={loadMoreMessages}
          hasMore={pagination?.hasMore || false}
          isLoadingMore={state.loading.more}
          unreadCount={pagination?.unreadCount || 0}
        />

        {activeConv && (
          <>
            {selectedFiles.length > 0 && (
              <AttachmentPreview>
                {selectedFiles.map((file, i) => (
                  <AttachmentItem key={i}>
                    <RemoveButton onClick={() => removeFile(i)}>
                      <X size={12} />
                    </RemoveButton>
                    {file.type.startsWith("image/") ? (
                      <img src={URL.createObjectURL(file)} alt="preview" />
                    ) : (
                      <video
                        src={URL.createObjectURL(file) + "#t=0.1"}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        preload="metadata"
                      />
                    )}
                  </AttachmentItem>
                ))}
              </AttachmentPreview>
            )}
            <InputContainer>
              <input
                type="file"
                multiple
                hidden
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*,video/*"
              />
              <Paperclip
                size={20}
                color="#999"
                style={{ cursor: "pointer" }}
                onClick={() => fileInputRef.current?.click()}
              />
              <ImageIcon
                size={20}
                color="#999"
                style={{ cursor: "pointer" }}
                onClick={() => fileInputRef.current?.click()}
              />
              <StyledInput
                value={inputText}
                onChange={(e) => handleTyping(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
              />
              <div
                onClick={handleSend}
                style={{
                  padding: "10px",
                  background: "#ff3135",
                  borderRadius: "50%",
                  color: "white",
                  cursor: "pointer",
                  display: "flex",
                }}
              >
                <Send size={18} />
              </div>
            </InputContainer>
          </>
        )}
      </div>
    </ChatContainer>
  );
};

export default ChatInterface;
